import React, { useEffect, useMemo, useState } from "react";
import { Doughnut } from "react-chartjs-2";
import moment from "moment";
import "bootstrap-daterangepicker/daterangepicker.css";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { Calendar } from "react-feather";
import ChartCard, { FilterSelect, ResetButton } from "./ChartCard";
import useChartRequest from "./useChartRequest";
import { SERIES_COLORS, doughnutOptions } from "./chartTheme";
import { getLoggedInUserTimezone } from "../../../../utils/utility";

const FREE_LABEL = "Free Slots";
const FREE_COLOR = "#e5eaf0";
const API_DATE = "DD/MM/YYYY";

export const formatMinutes = (minutes) => {
  const m = Math.max(0, Math.round(Number(minutes) || 0));
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h.toLocaleString("en-US")}h ${rest}m` : `${h.toLocaleString("en-US")}h`;
};

const buildRanges = () => ({
  "This month": [moment().startOf("month"), moment().endOf("month")],
  "Next 7 days": [moment(), moment().add(6, "days")],
  "Next 30 days": [moment(), moment().add(29, "days")],
  "Last 30 days": [moment().subtract(29, "days"), moment()],
  "Last month": [
    moment().subtract(1, "month").startOf("month"),
    moment().subtract(1, "month").endOf("month"),
  ],
});

const defaultRange = () => ({ start: moment().startOf("month"), end: moment().endOf("month") });

/**
 * Booked vs free time donut shared by the event and staff availability cards.
 * `filterKey` is the request field for the select (event_id / staff_id).
 */
const AvailabilityDonut = ({
  sendPostRequest,
  url,
  title,
  subtitle,
  tooltipCode,
  filterKey,
  filterOptions,
  filterPlaceholder,
}) => {
  const { rows, loading, refreshing, error, load } = useChartRequest(sendPostRequest, url);
  const [range, setRange] = useState(defaultRange);
  const [filterValue, setFilterValue] = useState("");
  const [pickerKey, setPickerKey] = useState(0);

  const request = (next = {}) => {
    const r = next.range || range;
    const f = next.filterValue !== undefined ? next.filterValue : filterValue;
    load({
      [filterKey]: f,
      start_date: r.start.format(API_DATE),
      end_date: r.end.format(API_DATE),
      timezone: getLoggedInUserTimezone(),
    });
  };

  useEffect(() => {
    request();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const segments = useMemo(() => {
    let colorIndex = 0;
    return rows.map((row) => {
      const isFree = row.label === FREE_LABEL;
      const color = isFree ? FREE_COLOR : SERIES_COLORS[colorIndex++ % SERIES_COLORS.length];
      return {
        label: isFree ? "Free time" : row.label,
        value: Math.max(0, Number(row.total) || 0),
        color,
        isFree,
      };
    });
  }, [rows]);

  const total = segments.reduce((sum, s) => sum + s.value, 0);
  const booked = segments.filter((s) => !s.isFree).reduce((sum, s) => sum + s.value, 0);
  const utilization = total > 0 ? Math.round((booked / total) * 100) : 0;
  const legend = [...segments.filter((s) => !s.isFree && s.value > 0), ...segments.filter((s) => s.isFree)];

  const chartData = useMemo(
    () => ({
      labels: segments.map((s) => s.label),
      datasets: [
        {
          data: segments.map((s) => s.value),
          backgroundColor: segments.map((s) => s.color),
          borderColor: "#fff",
          borderWidth: 2,
          hoverOffset: 4,
        },
      ],
    }),
    [segments]
  );

  const options = useMemo(() => doughnutOptions(formatMinutes), []);

  const actions = (
    <>
      <DateRangePicker
        key={pickerKey}
        initialSettings={{
          startDate: range.start,
          endDate: range.end,
          ranges: buildRanges(),
          alwaysShowCalendars: true,
          opens: "left",
          locale: { firstDay: 0, format: "MMM D, YYYY" },
        }}
        onCallback={(start, end) => {
          const next = { start: moment(start), end: moment(end) };
          setRange(next);
          request({ range: next });
        }}
      >
        <button type="button" className="dash-control dash-control--range">
          <Calendar size={14} />
          <span>
            {range.start.format("MMM D")} – {range.end.format("MMM D, YYYY")}
          </span>
        </button>
      </DateRangePicker>
      <FilterSelect
        value={filterValue}
        options={filterOptions}
        placeholder={filterPlaceholder}
        width={150}
        onChange={(v) => {
          setFilterValue(v);
          request({ filterValue: v });
        }}
      />
      <ResetButton
        onClick={() => {
          const next = defaultRange();
          setRange(next);
          setFilterValue("");
          setPickerKey((k) => k + 1);
          request({ range: next, filterValue: "" });
        }}
      />
    </>
  );

  return (
    <ChartCard
      title={title}
      subtitle={subtitle}
      tooltipCode={tooltipCode}
      actions={actions}
      loading={loading}
      refreshing={refreshing}
      error={error}
      empty={total === 0}
      emptyTitle="No availability in this range"
      emptyText="Set up availability or pick a different date range."
      skeleton="ring"
      height={null}
      className="dash-card--donut"
    >
      <div className="dash-donut">
        <div className="dash-donut__chart">
          <Doughnut data={chartData} options={options} />
          <div className="dash-donut__center">
            <strong>{utilization}%</strong>
            <span>Booked</span>
          </div>
        </div>
        <ul className="dash-donut__legend">
          {legend.map((s, i) => (
            <li key={`${s.label}-${i}`}>
              <span className="dash-donut__dot" style={{ background: s.isFree ? "#cbd5e1" : s.color }} />
              <span className="dash-donut__label" title={s.label}>
                {s.label}
              </span>
              <span className="dash-donut__value">{formatMinutes(s.value)}</span>
              <span className="dash-donut__pct">{total ? Math.round((s.value / total) * 100) : 0}%</span>
            </li>
          ))}
        </ul>
      </div>
    </ChartCard>
  );
};

export default AvailabilityDonut;
